import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalBody, ModalFooter, ModalHeader, Button } from 'shards-react';
import AsyncSelect from 'react-select/async';
import Errors from '../Errors';
import { ReadinessReport } from './ReadinessReport';
import { debouncedFetchAccounts } from '../../utils/accounts';
import CredentialMigrationService from '../../services/CredentialMigrationService';

export function MigrateCredentialModal({ open, toggle, credentialId, onMigrated }) {
  const [toAccount, setToAccount] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const latestRequest = useRef(0);

  const onAccountSelected = (option) => {
    setToAccount(option);
    setReadiness(null);
    setErrors([]);
    if (!option) return;

    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .readiness(credentialId, option.value)
      .then((data) => {
        // Ignores stale responses when the user picked another account in the meantime.
        if (latestRequest.current !== requestId) return;
        if (!data) {
          setErrors(['No se pudo obtener el diagnóstico de la migración.']);
          return;
        }
        setReadiness(data);
      })
      .catch(() => {
        if (latestRequest.current === requestId) {
          setErrors(['No se pudo obtener el diagnóstico de la migración.']);
        }
      });
  };

  const onClose = () => {
    if (submitting) return;
    latestRequest.current += 1;
    setToAccount(null);
    setReadiness(null);
    setErrors([]);
    toggle();
  };

  const onConfirm = () => {
    setErrors([]);
    setSubmitting(true);
    const credentialMigrationService = CredentialMigrationService();
    credentialMigrationService
      .start(credentialId, toAccount.value)
      .then((data) => {
        setSubmitting(false);
        if ((data.errors || []).length > 0) {
          setErrors(data.errors);
          return;
        }
        onMigrated(data.migrationId);
      })
      .catch(() => {
        setSubmitting(false);
        setErrors(['No se pudo iniciar la migración.']);
      });
  };

  return (
    <Modal open={open} size="lg" toggle={onClose}>
      <ModalHeader>Migrar cuenta</ModalHeader>
      <ModalBody>
        <Errors errors={errors} />
        <label htmlFor="toAccountId">Cuenta destino</label>
        <AsyncSelect
          cacheOptions={true}
          id="toAccountId"
          loadOptions={debouncedFetchAccounts}
          onChange={onAccountSelected}
          placeholder="Busca la cuenta destino por nombre"
          value={toAccount}
        />
        {readiness && (
          <div className="MarginTop20">
            <ReadinessReport readiness={readiness} />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button disabled={submitting} onClick={onClose} theme="secondary">
          Cancelar
        </Button>
        <Button
          disabled={
            !readiness ||
            !readiness.readyToMigrate ||
            submitting ||
            !toAccount ||
            readiness.toAccountId !== toAccount.value
          }
          onClick={onConfirm}
        >
          Confirmar migración
        </Button>
      </ModalFooter>
    </Modal>
  );
}

MigrateCredentialModal.propTypes = {
  credentialId: PropTypes.string.isRequired,
  onMigrated: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired
};
